<template>
<div>

	<div class="d-flex justify-content-between align-items-center mb-2">

		<info-box v-if="component.bid_component_id > 0" label="Direct Value" :value="componentDirectValue.toCurrency()"></info-box>

		<button v-if="!isFinalized" type="button" v-on:click="deleteComponent" class="btn btn-danger btn-sm" :disabled="childComponents.length > 0" :title="childComponents.length > 0 ? 'You need to delete all child components before deleting the parent' : 'Delete the component'">
			<i class="fa fa-trash"></i>
			Delete Component
		</button>

	</div>

	<accordion :heading="component.bid_component_id > 0 ? 'Component Details' : 'Category Details'">

		<div style="margin-bottom: 10px"></div>

		<field label="Name"
			col-class="col-sm-5 col-md-4"
			:align-label-right="true"
			v-model="componentFields.name"
			:readonly="isFinalized"
			:params="{
				type: 'text',
				maxlength: 80
			}"
			v-on:change="saveFields">
		</field>

		<template v-if="component.bid_component_id > 0">

			<component-detail-fields :values="componentFields"
				col-class="col-sm-5 col-md-4"
				:is-finalized="isFinalized"
				v-on:change="detailsFieldsChange">
			</component-detail-fields>
			
		</template>

	</accordion>
	<template v-if="component.bid_component_id > 0">

		<accordion :heading="'Material (' + materials.length + ')'">

			<material-section
				:materials="materials"
				:component="component"
				billing-document-object-resource="JobBidComponentObject"
				:billing-document-assemblies="assemblies"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">	
			</material-section>

		</accordion>

		<accordion :heading="'Labour (' + labour.length + ')'">

			<labour-section
				:labour="labour"
				:component="component"
				:hours-cap="componentFields.hours_cap"
				:total-material-labour-hours="totalMaterialLabourHours"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels"
				v-on:hoursCapChanged="hoursCapChanged">
			</labour-section>
			
		</accordion>

		<accordion :heading="'Equipment (' + equipment.length + ')'">

			<equipment-section
				:equipment="equipment"
				:component="component"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">
			</equipment-section>
			
		</accordion>

		<accordion :heading="'Supplier Quotes (' + supplierQuotes.length + ')'">

			<supplier-quotes-section
				:supplier-quotes="supplierQuotes"
				:component="component"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">
			</supplier-quotes-section>
			
		</accordion>

		<accordion :heading="'Additional Factors & Items (' + additionalFactors.length + ')'">

			<additional-factors-section 
				:additional-factors="additionalFactors"
				:component="component"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">		
			</additional-factors-section>
			
		</accordion>
	</template>

	<accordion :heading="'Child Components (' + childComponents.length + ')'" :stay-open="component.bid_component_id == 0">

		<div class="d-flex justify-content-between align-items-center  mt-2">

			<div>
				<p v-if="childComponents.length == 0" class="mb-0">No child components</p>
			</div>
			<div>
				<button v-if="!isFinalized" class="btn btn-success" v-on:click="addComponent">
					<i class="fa fa-plus"></i>
					Add Component
				</button>
			</div>
		</div>
		
		<draggable v-if="childComponents.length > 0"
			v-model="childComponents" 
			class="list-group mt-2"
			v-on:end="updateOrder">

			<li v-for="comp in childComponents"
				style="padding-bottom: 5px;padding-top: 5px;"
				class="list-group-item">

				<div class="d-flex justify-content-between align-items-center">
					<div style="flex-grow: 1;" class="clickable-item" v-on:click="openComponent(comp)">
						<i class="fa fa-link mr-2"></i>
						<span v-if="comp.item_number">{{ comp.item_number }}. </span>{{ comp.name }}
					</div>
					<div>
						<form-group label="Qty"
							col-class="col-auto"
							style="margin-bottom: 0;">

							<div class="d-flex align-items-center">

								<field style="width: 100px"
									:params="{type:'number'}"
									:readonly="isFinalized"
									v-model="comp.qty"
									v-on:change="qtyChange($event,comp.id)">
								</field>

								<div class="ml-2" style="width: 40px;">{{ comp.qty_unit }}</div>
							</div>

						</form-group>
					</div>
				</div>
			</li>
		</draggable>

		
	</accordion>
</div>
</template>
<script type="text/javascript">

import Accordion from "@/components/Accordion"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import InfoBox from "@/components/InfoBox"

import ComponentDetailFields from "@/pages/project/project_tabs/bidding/components/ComponentDetailFields"

import draggable from 'vuedraggable'

import AdditionalFactorsSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/AdditionalFactorsSection"
import MaterialSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/MaterialSection"
import SupplierQuotesSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/SupplierQuotesSection"
import EquipmentSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/EquipmentSection"
import LabourSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/LabourSection"

import ProjectBidMixins from "@/mixins/ProjectBidMixins.js"
import InvoiceMixins from "@/mixins/InvoiceMixins.js"

export default {
	name: "ProjectBidComponent",
	components: {
		Accordion,
		TableList,
		ModalDialog,
		Field,
		InfoBox,
		AdditionalFactorsSection,
		MaterialSection,
		SupplierQuotesSection,
		EquipmentSection,
		LabourSection,
		FormGroup,
		draggable,
		ComponentDetailFields
	},
	mixins: [ProjectBidMixins,InvoiceMixins],
	props: ["component","isFinalized"],
	data() {
		return {
			componentFields: {
				name: "",
				description: "",
				item_number: null,
				spec_number: null,
				hours_cap: null,
				qty: 1,
				qty_unit: 'each',
				is_reverse_calculated: 0
			},
			componentQty: 1,
			materials: [],
			assemblies: [],
			equipment: [],
			labour: [],
			additionalFactors: [],
			supplierQuotes: [],
			childComponents: []
		}
	},
	watch: {
		component() {
			this.initComponent()
			this.initObjects()
			this.initSubComponents()
		}	
	},
	computed: {
		totalMaterialLabourHours() {
			return this.materials.reduce((acc,item) => acc + (+item.labour_hrs * +item.qty),0)
		},
		componentDirectValue() {
			let total = 0, vm = this

			total += this.materials
				.map(mat => {
					if (!("rate" in mat) || +mat.rate == 0) {
						if ("material_price" in mat)
							mat.rate = +mat.material_price / +mat.per
						else if ("price" in mat)
							mat.rate = +mat.price / +mat.per

					} 

					return mat
				})
				.reduce((acc,item) => {

					let totalRounded = +item.rate * +item.qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			total += this.equipment.reduce((acc,item) => {

					let totalRounded = +item.rate * +item.qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			total += this.labour.reduce((acc,item) => {

					let qty = +item.hrs
					if ("hrs_per_person" in item && vm.componentFields.hours_cap == 0)
						qty = +item.hrs_per_person * +item.crew

					let totalRounded = +item.rate * qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			total += this.supplierQuotes.reduce((acc,item) => {

					let totalRounded = +item.rate * +item.qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			//total += this.additionalFactorsTotal

			let mutatedAdditionalFactors = this.additionalFactors.map(factor => {
				return vm.mutateAdditionalFactor(factor,vm.component.id)
			})

			total += mutatedAdditionalFactors.reduce((acc,item) => acc + item.totalRaw,0)

			return total
		}
	},
	methods: {
		openComponent(comp) {
			this.$emit("toComponent",comp)
		},
		initComponent() {
			let vm = this

			Object.keys(this.componentFields).forEach(key => {
				vm.componentFields[key] = vm.component[key]
			})
		},
		initSubComponents() {
			this.childComponents = this.component.JobBidComponent
		},
		initObjects(objects,type) {
			let vm = this

			let objMap = {
				Material: "materials",
				Assembly: "assemblies",
				EmployeeClassification: "labour",
				AdditionalFactor: "additionalFactors",
				Equipment: "equipment",
				SupplierQuote: 'supplierQuotes'
			}

			if (!type) {
				//reset all
				this.materials = []
				this.labour = []
				this.additionalFactors = []
				this.equipment = []
				this.supplierQuotes = []
				this.assemblies = []
			}
			else //only reset the defined type
				this[objMap[type]] = []

			let sourceObjects
			if (objects) 
				sourceObjects = objects
			else if ("JobBidComponentObject" in this.component && this.component.JobBidComponentObject) 
				sourceObjects = this.component.JobBidComponentObject

			if (sourceObjects) {

				sourceObjects.forEach(obj => {

					let objToAdd = {}

					try {
						objToAdd = JSON.parse(obj.field_values_json)
					}
					catch (e) {}

					objToAdd.qty = obj.qty
					objToAdd.rate = obj.rate
					objToAdd.price_adjustment = obj.price_adjustment
					objToAdd.markup = obj.markup
					objToAdd.objectRecordId = obj.id
					objToAdd.object_id = obj.object_id
					objToAdd.itemType = "itemType" in obj ? obj.itemType : null

					if (obj.type && obj.type in objMap)
						vm[objMap[obj.type]].push(objToAdd)
				})
			}
		},
		updateOrder() {
			let payload = {
				records: JSON.stringify(this.childComponents.map((comp,index) => {
					return {
						order_index: index,
						id: comp.id
					}
				}))
			}

			this.postData("JobBidComponent/save-batch",payload)
		},
		hoursCapChanged(value) {
			this.componentFields.hours_cap = value
			this.saveFields()
		},
		detailsFieldsChange(values) {
			this.componentFields = values

			this.saveFields()
		},
		saveFields() {
			let vm = this

			this.$nextTick(() => {

				let payload = vm.componentFields

				payload.id = vm.component.id

				vm.postData("JobBidComponent/save",payload,true).then(response => {
					if (response.success)
						vm.alertSaved("Changes saved")
				})

			})
		},
		addComponent() {
			this.$bus.$emit("addComponent",this.component.id)
		},
		deleteComponent() {
			let vm = this

			this.postData("JobBidComponent/delete",{id: this.component.id},true).then(response => {
				if (response.success)
					vm.$bus.$emit("componentDeleted",vm.component)
			})
		},
		updateObjectModels() {
			let vm = this

			this.fetchBidObjects(this.component.bid_id).then(responseObjects => {
				//filter to just this component
				let compObjects = responseObjects.filter(obj => obj.bid_component_id == vm.component.id)

				vm.initObjects(compObjects)
			})
		},
		fetchComponentObjects(typeFilter) {
			let vm = this

			let params = {
				filters: [
					["bid_component_id",vm.component.id]
				]
			}

			if (typeFilter)
				params.filters.push(["type",typeFilter])

			return new Promise((resolve,reject) => {

				vm.getJSON("JobBidComponentObject/get",params).then(response => {

					vm.initObjects(response.data,typeFilter)

					resolve(response)
				})

			})
		}
	},
	created() {
		let vm = this
		this.initComponent()
		this.initObjects()
		this.initSubComponents()

		if (this.$store.unitMeasurementOptions.length == 0) 
			this.fetchMeasurementUnits()
	}
}
</script>

<style scoped>
.clickable-item {
	cursor: pointer;
}

.clickable-item:hover {
	opacity: 0.7
}
</style>